import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MobileHeader, SearchInput, Card, EmptyState, ErrorState, Button,
} from '@parsina/framework';
import { Users } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatients } from '../../hooks/usePatients.js';
import { DEFAULT_PAGE_SIZE, clampPage, pageCount } from '../../lib/paging.js';
import { searchTerm, MIN_SEARCH_LENGTH } from '../../lib/patients.js';
import MobilePatientCard from '../../components/mobile/MobilePatientCard.jsx';
import { ShowMore } from '../../components/mobile/MobileKit.jsx';
import { PatientsSkeletonMobile } from './Patients.skeleton.jsx';

// بیماران — the mobile register (a top-level bottom-nav destination).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — title + count        │  scrolls away (§3.2, content screen)
// │ ② SearchInput — STICKY under it       │  the one inline control (§4B)
// │ ③ one card per patient                │  whole card taps into the record
// │ ④ «نمایش بیشتر»                        │  accumulates; never a desktop Pager
// │ [ BottomNav ]                          │
// └──────────────────────────────────────┘
//
// The desktop `Pager` is a row of 34px buttons — below the 2.75rem floor (§6) —
// and §4 rules out infinite scroll, so the list grows through an explicit
// expander. The `?page=` param still advances as it grows, so a refresh
// restores how far the dentist got, and `clampPage` still guards a hand-edited
// value.
//
// Everything about SCOPE is the desktop's, unchanged and load-bearing: no
// membership and pending membership are two different sentences, and `busy`
// (membership + list) is what stops «هنوز بیماری ثبت نشده» painting over a
// register that simply has not been asked for yet.

const PatientsMobile = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: clinicsLoading, activeId, scopeState } = useClinicScope();

  const urlQuery = searchParams.get('q') || '';
  const urlPage = Number(searchParams.get('page')) || 1;

  const [typed, setTyped] = React.useState(urlQuery);
  React.useEffect(() => setTyped(urlQuery), [urlQuery]);

  const commit = React.useCallback(
    (value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set('q', value);
      else next.delete('q');
      next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // The desktop's debounce, verbatim: 300 ms, and a CLEAR commits immediately
  // because waiting to show the full list again reads as a broken clear button.
  React.useEffect(() => {
    const trimmed = typed.trim();
    if (trimmed === urlQuery) return undefined;
    if (!trimmed) {
      commit('');
      return undefined;
    }
    if (trimmed.length < MIN_SEARCH_LENGTH) return undefined;
    const timer = setTimeout(() => commit(trimmed), 300);
    return () => clearTimeout(timer);
  }, [typed, urlQuery, commit]);

  const { patients, total, loading, error, reload } = usePatients({
    clinicId: activeId,
    search: searchTerm(urlQuery),
    page: urlPage,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const busy = clinicsLoading || loading;
  const page = clampPage(urlPage, total, DEFAULT_PAGE_SIZE);

  // The accumulated list. A phone reads a register by scrolling, so page 2 is
  // APPENDED rather than replacing page 1 — but the pages themselves are the
  // desktop's, server-side, 25 at a time.
  //
  // Keyed on (clinic, query): changing either is a different register and the
  // accumulation has to start over, or the dentist sees clinic A's page 2 under
  // clinic B's page 1.
  const listKey = `${activeId || ''}|${urlQuery}`;
  const [acc, setAcc] = React.useState({ key: listKey, rows: [] });

  React.useEffect(() => {
    setAcc((prev) => {
      if (prev.key !== listKey) return { key: listKey, rows: [] };
      return prev;
    });
  }, [listKey]);

  React.useEffect(() => {
    if (busy || error) return;
    setAcc((prev) => {
      const base = prev.key === listKey ? prev.rows : [];
      const seen = new Set(base.map((p) => p.id));
      const merged = [...base, ...patients.filter((p) => !seen.has(p.id))];
      // Identical content must not produce a new array, or this effect loops.
      if (merged.length === base.length && prev.key === listKey) return prev;
      return { key: listKey, rows: merged };
    });
  }, [patients, busy, error, listKey]);

  const rows = acc.key === listKey ? acc.rows : [];
  const hasMore = page < pageCount(total, DEFAULT_PAGE_SIZE);

  const loadMore = React.useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page + 1));
    // NOT `replace`: «نمایش بیشتر» is a step the back button should undo, the
    // same way the desktop pager's next-page is.
    setSearchParams(params);
  }, [page, searchParams, setSearchParams]);

  const noClinic = !clinicsLoading && scopeState === 'none';
  const pendingClinic = !clinicsLoading && scopeState === 'pending';
  const blocked = noClinic || pendingClinic;

  const trimmedTyped = typed.trim();
  const tooShort = trimmedTyped.length > 0 && trimmedTyped.length < MIN_SEARCH_LENGTH;

  // The first page is a skeleton; a LATER page keeps the rows on screen and
  // dims (§7: "stale-while-refetch dims, never blanks").
  const firstLoad = busy && rows.length === 0;

  return (
    // ONE scroll region, and the header is INSIDE it: §3.2 lets a content
    // screen's header scroll away — content wins the viewport, chrome does not.
    // What stays is the search field, which is sticky at the top of this same
    // scroller.
    <div
      data-testid="patients-content"
      data-state={error ? 'error' : firstLoad ? 'loading' : rows.length ? 'content' : 'empty'}
      aria-busy={busy || undefined}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MobileHeader
        title={t('patients.title')}
        subtitle={!blocked && !busy && !error ? t('patients.count', { count: total }) : undefined}
        data-testid="patients-header"
      />

      {blocked ? (
          <div style={{ padding: '0.875rem 0.75rem 1.5rem' }}>
            <Card>
              <EmptyState
                icon={<Users size={44} color={C.textMuted} />}
                title={t(pendingClinic ? 'patients.pendingClinic.title' : 'patients.noClinic.title')}
                description={t(
                  pendingClinic
                    ? 'patients.pendingClinic.description'
                    : 'patients.noClinic.description',
                )}
                action={
                  <Button className="min-h-11" onClick={() => navigate('profile')}>
                    {t('patients.noClinic.action')}
                  </Button>
                }
              />
            </Card>
          </div>
        ) : (
          <>
            {/* The two most-used controls stay inline (§4B). Here there is only
                one — free-text search — and it is sticky so scrolling a long
                register never puts it out of reach. */}
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                background: C.bg,
                padding: '0.75rem',
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <SearchInput
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={t('patients.search.placeholder')}
                aria-label={t('patients.search.placeholder')}
                clearLabel={t('common.actions.clearFilters')}
                // The kit hardcodes dir="rtl" on its <input>. This field accepts
                // «علی», "Ali", a phone number and a file number, and a Latin or
                // digit run typed into a forced-RTL box has its caret and its
                // punctuation reordered. `inputProps` spreads last, so it wins.
                // `fontSize: 1rem` is the iOS focus-zoom floor (§6).
                inputProps={{ dir: 'auto', style: { fontSize: '1rem' } }}
                data-testid="patients-search"
                style={{ width: '100%' }}
              />
              {tooShort && (
                <div style={{ marginTop: '0.375rem', fontSize: '0.6875rem', color: C.textMuted }}>
                  {t('patients.search.hint')}
                </div>
              )}
            </div>

            <div style={{ flex: 1, padding: '0.75rem 0.75rem 1.5rem' }}>
              {error ? (
                <div role="alert">
                  <ErrorState
                    title={t('patients.error.title')}
                    description={t('errors.load.patients')}
                    onRetry={reload}
                    retryLabel={t('common.actions.retry')}
                  />
                </div>
              ) : firstLoad ? (
                // Patient-card-shaped rows, not a generic card stack: the
                // register's own shape, so nothing shifts when the page lands.
                <PatientsSkeletonMobile label={t('common.loading')} />
              ) : rows.length === 0 ? (
                <Card>
                  {/* A filtered empty list and a genuinely empty register are
                      different facts, and only one of them means "register
                      someone". */}
                  {urlQuery ? (
                    <EmptyState
                      icon={<Users size={44} color={C.textMuted} />}
                      title={t('patients.emptyFiltered.title')}
                      description={t('patients.emptyFiltered.description')}
                      action={
                        <Button variant="outline" className="min-h-11" onClick={() => setTyped('')}>
                          {t('common.actions.clearFilters')}
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<Users size={44} color={C.textMuted} />}
                      title={t('patients.empty.title')}
                      description={t('patients.empty.description')}
                    />
                  )}
                </Card>
              ) : (
                <div style={{ opacity: busy ? 0.6 : 1, transition: 'opacity .15s' }}>
                  {rows.map((p) => (
                    <MobilePatientCard
                      key={p.id}
                      patient={p}
                      onOpen={() => navigate('patient', { id: p.id })}
                      testId={`patients-row-${p.id}`}
                    />
                  ))}
                  {hasMore && <ShowMore onClick={loadMore} testId="patients-more" />}
                  <div
                    style={{
                      marginTop: '0.75rem',
                      textAlign: 'center',
                      fontSize: '0.6875rem',
                      color: C.textMuted,
                    }}
                  >
                    {t('patients.count', { count: total })}
                  </div>
                </div>
              )}
            </div>
          </>
      )}
    </div>
  );
};

export default PatientsMobile;
